import React, { useEffect, useState } from 'react';
import Btn from '../../../components/ui/Btn';
import Field, { TextInput, TextArea, NativeSelect } from '../../../components/ui/Field';

const DEFAULTS = {
  cap_message: 'Por ahora no puedo atenderte. Intenta de nuevo más tarde.',
  unsupported_message: 'Por ahora solo puedo leer mensajes de texto. ¿Me lo puedes escribir?',
  fallback_message: 'Déjame pasar tu caso a una persona del equipo para ayudarte mejor.',
  audio_error_message: 'No pude escuchar bien tu audio. ¿Me lo puedes escribir o mandar de nuevo?',
};

const fromConfig = (agent, cfg = {}) => ({
  model: agent.model,
  cap: agent.monthly_cap_usd != null ? String(agent.monthly_cap_usd) : '',
  humanHelp: Array.isArray(cfg.tools) ? cfg.tools.includes('request_human_help') : true,
  maxHistory: cfg.max_history != null ? String(cfg.max_history) : '20',
  pauseHours: cfg.handoff_pause_hours != null ? String(cfg.handoff_pause_hours) : '12',
  cap_message: cfg.cap_message || '',
  unsupported_message: cfg.unsupported_message || '',
  fallback_message: cfg.fallback_message || '',
  audio_error_message: cfg.audio_error_message || '',
  typing: cfg.typing_indicator !== false,
  audio: cfg.audio_enabled !== false,
});

// Comportamiento: modelo, tope de gasto, herramienta de ayuda humana, memoria de la conversación y mensajes fijos.
export default function BehaviorPanel({ agent, draft, models, onSave }) {
  const [f, setF] = useState(() => fromConfig(agent, draft?.config));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => { setF(fromConfig(agent, draft?.config)); }, [agent, draft?.id, draft?.config]);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const capNum = f.cap === '' ? null : Number(f.cap);
  const histNum = Number(f.maxHistory);
  const capError = f.cap !== '' && (!Number.isFinite(capNum) || capNum < 0.01) ? 'Escribe un monto en dólares desde 0.01, o déjalo vacío para no poner tope.' : '';
  const histError = !Number.isInteger(histNum) || histNum < 1 || histNum > 40 ? 'Entre 1 y 40 mensajes.' : '';
  const pauseNum = Number(f.pauseHours);
  const pauseError = !Number.isInteger(pauseNum) || pauseNum < 0 || pauseNum > 168 ? 'Entre 0 y 168 horas (una semana).' : '';

  const save = async () => {
    if (capError || histError || pauseError) return;
    setBusy(true); setError('');
    const cfg = { ...(draft?.config || {}) };
    cfg.tools = f.humanHelp ? ['request_human_help'] : [];
    cfg.max_history = histNum;
    cfg.handoff_pause_hours = pauseNum;
    cfg.typing_indicator = f.typing;
    cfg.audio_enabled = f.audio;
    for (const k of ['cap_message', 'unsupported_message', 'fallback_message', 'audio_error_message']) { if (f[k].trim()) cfg[k] = f[k].trim(); else delete cfg[k]; }
    try { await onSave({ agentPatch: { model: f.model, monthly_cap_usd: capNum }, config: cfg }); }
    catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  return (
    <div className="max-w-3xl space-y-8">
      <section className="grid sm:grid-cols-2 gap-4">
        <Field label="Modelo" hint="Cambia en cuanto guardes; no necesita publicarse.">
          <NativeSelect value={f.model} onChange={set('model')}>
            {[...new Set([f.model, ...models])].map((m) => <option key={m} value={m}>{m}</option>)}
          </NativeSelect>
        </Field>
        <Field label="Tope de gasto al mes (USD)" optional error={capError} hint="Al llegar al tope responde el mensaje fijo de abajo y no gasta más.">
          <TextInput inputMode="decimal" value={f.cap} onChange={set('cap')} placeholder="Sin tope" />
        </Field>
      </section>

      <section className="space-y-4">
        <label className="flex items-start gap-3 cursor-pointer rounded-lg border border-line p-4 hover:border-fg/15">
          <input type="checkbox" checked={f.humanHelp} onChange={set('humanHelp')}
            className="mt-0.5 h-4 w-4 accent-[rgb(var(--live))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-live/60" />
          <span>
            <span className="block text-sm font-medium text-fg">Puede pedir ayuda a una persona</span>
            <span className="block text-[13px] text-fg-dim mt-0.5">Cuando alguien pide hablar con una persona o el agente no sabe resolver, le pasa la conversación al equipo: aparece con alerta en Chats del dashboard y el agente le dice cuándo le responderán, según el horario de atención del proyecto.</span>
          </span>
        </label>
        {f.humanHelp && (
          <Field label="Horas que el agente se queda callado al pasarla" error={pauseError}
            hint="Mientras, responde una persona desde Chats. Si nadie contesta en ese tiempo, el agente vuelve a atender solo. 0 = solo avisa y sigue respondiendo. Solo aplica en proyectos con el motor encendido.">
            <TextInput inputMode="numeric" value={f.pauseHours} onChange={set('pauseHours')} className="sm:w-32" />
          </Field>
        )}
        <Toggle checked={f.typing} onChange={set('typing')} title="Mostrar «escribiendo…» mientras prepara la respuesta"
          text="En WhatsApp además marca el mensaje como leído (palomitas azules). En Telegram aparece «escribiendo…» arriba del chat. El chat web siempre muestra «Pensando…»." />
        <Toggle checked={f.audio} onChange={set('audio')} title="Escuchar audios y notas de voz"
          text="En WhatsApp y Telegram: el audio se transcribe con la clave de OpenAI de este agente y el agente responde como si se lo hubieran escrito. En Chats se ve como «🎤 lo que dijo». Con el modelo de transcripción por defecto cuesta menos de un centavo de dólar por minuto de audio." />
        <Field label="Mensajes que recuerda de la conversación" error={histError} hint="Más contexto da mejores respuestas pero cuesta más. 20 es un buen punto de partida.">
          <TextInput inputMode="numeric" value={f.maxHistory} onChange={set('maxHistory')} className="sm:w-32" />
        </Field>
      </section>

      <section className="space-y-4">
        <h3 className="text-[15px] font-semibold text-fg">Mensajes fijos</h3>
        <p className="-mt-2 text-[13px] text-fg-dim">Respuestas que no pasan por el modelo. Vacío usa el texto de ejemplo.</p>
        <Field label="Cuando llega a su tope de gasto"><TextArea rows={2} value={f.cap_message} onChange={set('cap_message')} placeholder={DEFAULTS.cap_message} /></Field>
        <Field label={f.audio ? 'Cuando le mandan imagen o documento' : 'Cuando le mandan audio, imagen o documento'}><TextArea rows={2} value={f.unsupported_message} onChange={set('unsupported_message')} placeholder={DEFAULTS.unsupported_message} /></Field>
        {f.audio && <Field label="Cuando no logra entender un audio" hint="Audio en silencio, muy largo (más de 10 minutos) o en un formato que no se puede leer."><TextArea rows={2} value={f.audio_error_message} onChange={set('audio_error_message')} placeholder={DEFAULTS.audio_error_message} /></Field>}
        <Field label="Cuando no logra responder"><TextArea rows={2} value={f.fallback_message} onChange={set('fallback_message')} placeholder={DEFAULTS.fallback_message} /></Field>
      </section>

      <div className="flex items-center gap-3">
        <Btn variant="primary" onClick={save} loading={busy} disabled={Boolean(capError || histError || pauseError)}>Guardar comportamiento</Btn>
        <span className="text-[12.5px] text-fg-faint">Los mensajes y la ayuda humana se guardan en el borrador: publícalo para usarlos.</span>
        {error && <span role="alert" className="text-[12.5px] text-signal">{error}</span>}
      </div>
    </div>
  );
}

function Toggle({ checked, onChange, title, text }) {
  return (
    <label className="flex items-start gap-3 cursor-pointer rounded-lg border border-line p-4 hover:border-fg/15">
      <input type="checkbox" checked={checked} onChange={onChange}
        className="mt-0.5 h-4 w-4 accent-[rgb(var(--live))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-live/60" />
      <span>
        <span className="block text-sm font-medium text-fg">{title}</span>
        <span className="block text-[13px] text-fg-dim mt-0.5">{text}</span>
      </span>
    </label>
  );
}
